import React, { useEffect, useMemo, useState } from 'react'
import TribeHeaderContainer from '../../components/TribeOrg/TribeHeaderContainer'
import TribeLeadershipContainer from '../../components/TribeOrg/TribeLeadershipContainer'
import TribeProjectContainer from '../../components/TribeOrg/TribeProjectContainer'
import { LeadershipGroup, Member } from '../../types/Tribe'
import {
  engagementsDefaultValues,
  leadershipDefaultValues,
} from '../../utils/tribeOrgDefaultValues'
import { UserTeam } from '../../types/UserTeam'
import { useLazyGetAllGuildsQuery } from '../../redux/guild.endpoints'
import { AllGuildTypes, Guild } from '../../types/Guilds'
import {
  isEngagementActiveInRange,
  isTeamActiveInRange,
  isUserActiveOnDay,
} from '../../utils/general'

interface Props {
  tribesOrg: any
  isSnapshot: boolean
  startDate: string
  endDate: string
  readOnly?: boolean
}

const NO_LEADERSHIP_KEY = 'no-leadership'

const GUILD_DEPT_ORDER = [
  AllGuildTypes.Developer,
  AllGuildTypes.QA,
  AllGuildTypes.Design,
  AllGuildTypes.DevOps,
] as const

const getLeaderName = (
  leadership: LeadershipGroup | Record<string | number, Member[]>,
  guildType: string,
  guilds: Guild[],
): string => {
  const guildId = guilds.find(guild => guild.type === guildType)?.id ?? -1
  const leaders =
    (leadership as Record<string | number, Member[]>)[guildId] ??
    (leadership as Record<string | number, Member[]>)[guildType] ??
    []
  const leader = leaders[0]
  if (!leader || leader.id === -1) return '---'
  return `${leader.firstName} ${leader.lastName}`.trim()
}

const TribeOrgContainer = ({
  tribesOrg,
  isSnapshot,
  startDate,
  endDate,
  readOnly = false,
}: Props) => {
  const [currentTribeOrg, setCurrentTribeOrg] = useState<any>({
    name: '',
    updated_at: new Date(),
    teams: [],
  })
  const [teamsMap, setTeamsMap] = useState(new Map())
  const [getAllGuilds, getAllGuildsResponse] = useLazyGetAllGuildsQuery()
  const [guilds, setGuilds] = useState<Array<Guild>>([])

  useEffect(() => {
    getAllGuilds()
  }, [])

  useEffect(() => {
    setCurrentTribeOrg(tribesOrg)
  }, [tribesOrg])

  useEffect(() => {
    if (getAllGuildsResponse && getAllGuildsResponse.data) {
      setGuilds(getAllGuildsResponse.data)
    }
  }, [getAllGuildsResponse])

  useEffect(() => {
    if (currentTribeOrg?.teams) {
      setTeamsMap(buildTeamDictionary(currentTribeOrg))
    }
  }, [currentTribeOrg, guilds, isSnapshot, startDate, endDate])

  const getGuildIdWithType = (guildType: string) => {
    if (guilds && guilds.length > 0) {
      return guilds.find(guild => guild.type === guildType)?.id ?? -1
    }
    return -1
  }

  const leadershipGuildIds = [
    getGuildIdWithType(AllGuildTypes.SM),
    getGuildIdWithType(AllGuildTypes.BA),
    getGuildIdWithType(AllGuildTypes.ADM),
  ]

  const guildsInfo = [
    {
      name: AllGuildTypes.Developer,
      color: '#e2d9f3',
      guildId: getGuildIdWithType(AllGuildTypes.Developer),
    },
    {
      name: AllGuildTypes.QA,
      color: '#FFE5D0',
      guildId: getGuildIdWithType(AllGuildTypes.QA),
    },
    {
      name: AllGuildTypes.Design,
      color: '#D2F4EA',
      guildId: getGuildIdWithType(AllGuildTypes.Design),
    },
    {
      name: AllGuildTypes.DevOps,
      color: '#E7F1FF',
      guildId: getGuildIdWithType(AllGuildTypes.DevOps),
    },
  ]

  const getTeamMembersByGuildType = (
    teamMembers: Member[],
    guildType: string,
  ) => {
    if (!guilds.length) return []

    const guildId = getGuildIdWithType(guildType)
    const filteredTeamMembers = teamMembers.filter(
      member => member.guildId === guildId,
    )
    filteredTeamMembers.sort((a, b) => a.firstName.localeCompare(b.firstName))

    return filteredTeamMembers
  }

  const getNonLeadershipMembers = (teamAllocations: UserTeam[]): Member[] => {
    const members: Member[] = []
    teamAllocations
      ?.filter(item => isUserActiveOnDay(item.user?.end_on, isSnapshot))
      .forEach(allocation => {
        if (!leadershipGuildIds.some(guildId => allocation.user.guildId === guildId)) {
          members.push({ ...allocation.user, role: allocation.role })
        }
      })
    return members
  }

  const getTeamMembersFromAllocations = (teamAllocations: UserTeam[]) => {
    const members = getNonLeadershipMembers(teamAllocations)

    if (!members.length) return undefined

    return {
      [AllGuildTypes.Developer]: getTeamMembersByGuildType(
        members,
        AllGuildTypes.Developer,
      ),
      [AllGuildTypes.QA]: getTeamMembersByGuildType(members, AllGuildTypes.QA),
      [AllGuildTypes.Design]: getTeamMembersByGuildType(
        members,
        AllGuildTypes.Design,
      ),
      [AllGuildTypes.DevOps]: getTeamMembersByGuildType(
        members,
        AllGuildTypes.DevOps,
      ),
    }
  }

  const getTeamLeadership = team => {
    const leaderShip: Member[] = []
    let leaders: Array<Member> = []

    team.teamAllocations
      ?.filter(item => isUserActiveOnDay(item.user?.end_on, isSnapshot))
      .forEach(member => {
        if (leadershipGuildIds.some(guildId => member.user.guildId === guildId)) {
          leaderShip.push(member.user)
        }
      })

    if (leaderShip.length > 0) {
      const groupByGuild = leaderShip.reduce((group, product) => {
        const { guildId } = product
        group[guildId] = group[guildId] ?? []
        group[guildId].push(product)
        return group
      }, {})
      return groupByGuild as LeadershipGroup
    }

    leaders = [
      {
        ...leadershipDefaultValues[AllGuildTypes.ADM],
        id: -1,
        guildId: getGuildIdWithType(AllGuildTypes.ADM),
      },
      {
        ...leadershipDefaultValues[AllGuildTypes.BA],
        id: -1,
        guildId: getGuildIdWithType(AllGuildTypes.BA),
      },
      {
        ...leadershipDefaultValues[AllGuildTypes.SM],
        id: -1,
        guildId: getGuildIdWithType(AllGuildTypes.SM),
      },
    ]

    const groupByGuild = leaders.reduce((group, product) => {
      const { guildId } = product
      group[guilds.find(guild => guild.id === guildId)?.type ?? ''] =
        group[guildId] ?? []
      group[guilds.find(guild => guild.id === guildId)?.type ?? ''].push(
        product,
      )
      return group
    }, {})

    return groupByGuild as LeadershipGroup
  }

  const buildTeamDictionary = tribesOrgData => {
    const map = new Map()
    const sortedTeams = [...tribesOrgData.teams].sort(
      (a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name),
    )

    sortedTeams.forEach(team => {
      const setSet = new Set()
      team.teamAllocations
        ?.filter(item => isUserActiveOnDay(item.user?.end_on, isSnapshot))
        .forEach(element => {
          if (leadershipGuildIds.some(guildId => element.user.guildId === guildId)) {
            setSet.add(element.userId)
          }
        })
      const setKey = Array.from(setSet)
        .sort((a, b) => (a as number) - (b as number))
        .join('-')

      if (!isTeamActiveInRange(team, startDate, endDate)) return

      const filteredTeam = {
        ...team,
        engagements: team.engagements.filter(engagement =>
          isEngagementActiveInRange(engagement, startDate, endDate),
        ),
      }

      const groupKey = setKey || NO_LEADERSHIP_KEY

      if (map.has(groupKey)) {
        map.get(groupKey).push(filteredTeam)
      } else {
        map.set(groupKey, [filteredTeam])
      }
    })

    return map
  }

  const leadershipKeys = Array.from(teamsMap.keys()).sort()

  const allTeams = useMemo(() => {
    const teams: any[] = []
    leadershipKeys.forEach(key => {
      teamsMap.get(key)?.forEach(team => teams.push(team))
    })
    return teams
  }, [leadershipKeys, teamsMap])

  const getLeadershipForGuild = (
    leadership: LeadershipGroup | Record<string | number, Member[]>,
    guildType: string,
  ): Member[] => {
    const guildId = getGuildIdWithType(guildType)
    const fromId = (leadership as Record<number, Member[]>)[guildId]
    if (fromId?.length) return fromId
    const fromType = (leadership as Record<string, Member[]>)[guildType]
    if (fromType?.length) return fromType
    return [{ ...leadershipDefaultValues[guildType], id: -1, guildId }]
  }

  const sharedLeadership = useMemo(() => {
    if (allTeams.length <= 1 || !guilds.length) return null

    const fingerprints = allTeams.map(team => {
      const leadership = getTeamLeadership(team)
      return {
        adm: getLeaderName(leadership, AllGuildTypes.ADM, guilds),
        ba: getLeaderName(leadership, AllGuildTypes.BA, guilds),
        sm: getLeaderName(leadership, AllGuildTypes.SM, guilds),
      }
    })

    const first = fingerprints[0]
    const allMatch = fingerprints.every(
      fp => fp.adm === first.adm && fp.ba === first.ba && fp.sm === first.sm,
    )

    if (!allMatch) return null

    const leadership = getTeamLeadership(allTeams[0])
    return {
      adm: getLeadershipForGuild(leadership, AllGuildTypes.ADM),
      ba: getLeadershipForGuild(leadership, AllGuildTypes.BA),
      sm: getLeadershipForGuild(leadership, AllGuildTypes.SM),
    }
  }, [allTeams, guilds])

  const maxPerDept = useMemo(() => {
    const counts: Record<string, number> = {}
    GUILD_DEPT_ORDER.forEach(guildType => {
      counts[guildType] = 0
    })

    if (!guilds.length) return counts

    leadershipKeys.forEach(key => {
      teamsMap.get(key)?.forEach(team => {
        const members = getNonLeadershipMembers(team.teamAllocations)
        if (!members.length) return

        GUILD_DEPT_ORDER.forEach(guildType => {
          const guildId = getGuildIdWithType(guildType)
          const guildCount = members.filter(
            member => member.guildId === guildId,
          ).length
          counts[guildType] = Math.max(counts[guildType], guildCount)
        })
      })
    })

    return counts
  }, [leadershipKeys, teamsMap, guilds, isSnapshot])

  const admLabel = guilds.find(g => g.type === AllGuildTypes.ADM)?.role ?? 'ADM'
  const baLabel = guilds.find(g => g.type === AllGuildTypes.BA)?.role ?? 'BA'
  const smLabel =
    guilds.find(g => g.type === AllGuildTypes.SM)?.role ?? 'Scrum Master'

  return (
    <div className="tribe-org__chart">
      <TribeHeaderContainer tribeName={currentTribeOrg.name} />

      {sharedLeadership && (
        <TribeLeadershipContainer
          adm={sharedLeadership.adm}
          ba={sharedLeadership.ba}
          sm={sharedLeadership.sm}
          admLabel={admLabel}
          baLabel={baLabel}
          smLabel={smLabel}
          readOnly={readOnly}
          variant="shared"
        />
      )}

      <div
        className="tribe-org__columns"
        style={
          {
            '--total-team-count': Math.max(allTeams.length, 1),
          } as React.CSSProperties
        }
      >
        {sharedLeadership
          ? allTeams.map((team: any, index: number) => (
              <div
                key={`${currentTribeOrg.name}-${team.id ?? index}`}
                className="tribe-org__column"
              >
                <div className="tribe-org__column-projects">
                  <TribeProjectContainer
                    engagement={
                      team.engagements.length > 0
                        ? team.engagements[0]
                        : engagementsDefaultValues
                    }
                    team={getTeamMembersFromAllocations(team.teamAllocations)}
                    guildsInfo={guildsInfo}
                    maxPerDept={maxPerDept}
                    teamName={team.name}
                    readOnly={readOnly}
                  />
                </div>
              </div>
            ))
          : leadershipKeys.map(key => {
              const teams = teamsMap.get(key) ?? []
              const columnLeadership =
                teams.length > 0 ? getTeamLeadership(teams[0]) : null

              return (
                <div
                  key={`${currentTribeOrg.name}-${key}`}
                  className="tribe-org__column tribe-org__leadership-group"
                  style={
                    {
                      '--team-count': Math.max(teams.length, 1),
                    } as React.CSSProperties
                  }
                >
                  {columnLeadership && (
                    <TribeLeadershipContainer
                      adm={getLeadershipForGuild(
                        columnLeadership,
                        AllGuildTypes.ADM,
                      )}
                      ba={getLeadershipForGuild(
                        columnLeadership,
                        AllGuildTypes.BA,
                      )}
                      sm={getLeadershipForGuild(
                        columnLeadership,
                        AllGuildTypes.SM,
                      )}
                      admLabel={admLabel}
                      baLabel={baLabel}
                      smLabel={smLabel}
                      readOnly={readOnly}
                      variant="column"
                    />
                  )}

                  <div className="tribe-org__column-projects tribe-org__column-projects--grouped">
                    {teams.map((team: any, index: number) => (
                      <div
                        key={`${key}-${index}`}
                        className="tribe-org__team-column"
                      >
                        <TribeProjectContainer
                          engagement={
                            team.engagements.length > 0
                              ? team.engagements[0]
                              : engagementsDefaultValues
                          }
                          team={getTeamMembersFromAllocations(
                            team.teamAllocations,
                          )}
                          guildsInfo={guildsInfo}
                          maxPerDept={maxPerDept}
                          teamName={team.name}
                          readOnly={readOnly}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
      </div>
    </div>
  )
}

export default TribeOrgContainer
